import {
  Badge, Button, Card, Chip, Collapse, Grid, Group, MultiSelect, NumberInput, Select, Modal, SegmentedControl, SimpleGrid, Stack, Text, TextInput, Title, Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconAdjustments, IconBuildingCommunity, IconCube, IconLayoutGrid, IconMap2, IconPlus, IconSearch, IconTable, IconTrees, IconUserPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { ChoroplethMap } from "../../components/ChoroplethMap";
import { DataTable } from "../../components/DataTable";
import { Empty, fmtNum } from "../../components/ui";
import { useOpstina } from "../../context/opstina";
import { useT } from "../../i18n/script";
import MapPage from "../MapPage";
import { type Producer, ProducerForm } from "../producers/ProducersPage";
import AccessPage from "../public/AccessPage";

/** Boje vrsta voća (prepoznatljive, dovoljno različite i za daltoniste u kombinaciji sa nazivom). */
export const SPECIES_COLOR: Record<string, string> = {
  JABUKA: "#c8453a", KRUSKA: "#a3b236", SLJIVA: "#6d4c9f", TRESNJA: "#9e1b32", VISNJA: "#d6336c", BRESKVA: "#f08c00",
  KAJSIJA: "#f59f00", ORAH: "#7a5a3c", LJESNIK: "#b08a5a", MALINA: "#e64980", BOROVNICA: "#3b5bdb", KUPINA: "#343a40",
  DUNJA: "#e8c547", JAGODA: "#fa5252",
};
export const speciesColor = (s?: string | null) => SPECIES_COLOR[(s ?? "").toUpperCase()] ?? "#5c7c63";

export function useSpeciesNames() {
  const q = useQuery({ queryKey: ["vrsta_voca"], staleTime: 600_000,
    queryFn: () => http.get<{ code: string; name: string }[]>("/api/v1/codelists/vrsta_voca/items") });
  return (code?: string | null) => q.data?.find((x) => x.code === (code ?? "").toUpperCase())?.name ?? code ?? "";
}

interface OrchardRow {
  id: number; name: string; species: string; cultivar: string | null; producer: string | null; bpg: string | null; opstina: string | null;
  area_ha: number; tree_count: number; planting_year: number | null; holding_id: number | null; organic?: boolean; hail_net?: boolean;
  has_3d?: boolean; photos?: number; training_system?: string | null; rootstock?: string | null;
}

/** Kriterijumi napredne pretrage (voćnjak i gazdinstvo). Prazno = ne filtrira. */
interface Criteria {
  cultivars: string[]; rootstocks: string[]; yearFrom: number | ""; yearTo: number | ""; areaFrom: number | ""; areaTo: number | "";
  organic: string; hailNet: string; has3d: string; photos: string; category: string | null; rpg: string | null; form: string | null;
}
const EMPTY: Criteria = { cultivars: [], rootstocks: [], yearFrom: "", yearTo: "", areaFrom: "", areaTo: "", organic: "", hailNet: "",
  has3d: "", photos: "", category: null, rpg: null, form: null };
const yn = (v: string, b: boolean | undefined) => !v || (v === "da" ? !!b : !b);
function orchardMatches(o: OrchardRow, c: Criteria) {
  return (!c.cultivars.length || c.cultivars.includes(o.cultivar ?? "")) && (!c.rootstocks.length || c.rootstocks.includes(o.rootstock ?? ""))
    && (c.yearFrom === "" || (o.planting_year ?? 0) >= c.yearFrom) && (c.yearTo === "" || (o.planting_year ?? 9999) <= c.yearTo)
    && (c.areaFrom === "" || Number(o.area_ha) >= c.areaFrom) && (c.areaTo === "" || Number(o.area_ha) <= c.areaTo)
    && yn(c.organic, o.organic) && yn(c.hailNet, o.hail_net) && yn(c.has3d, o.has_3d) && yn(c.photos, (o.photos ?? 0) > 0);
}
const orchardCriteriaSet = (c: Criteria) => !!(c.cultivars.length || c.rootstocks.length || c.yearFrom !== "" || c.yearTo !== ""
  || c.areaFrom !== "" || c.areaTo !== "" || c.organic || c.hailNet || c.has3d || c.photos);
const activeCount = (c: Criteria) => [c.cultivars.length, c.rootstocks.length, c.yearFrom !== "" || c.yearTo !== "", c.areaFrom !== "" || c.areaTo !== "",
  c.organic, c.hailNet, c.has3d, c.photos, c.category, c.rpg, c.form].filter(Boolean).length;
type View = "opstine" | "kartice" | "tabela" | "vocnjaci" | "mapa";

/**
 * Katalog gazdinstava — zajednička ulazna tačka za sve korisnike (Agencija vidi sve u nadležnosti, proizvođač svoje).
 * Alfanumerički (kartice, tabela, voćnjaci) i grafički (mapa → voćnjak → 3D); „Prikaži na mapi“ prenosi rezultat pretrage.
 */
export default function HoldingsPage() {
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const sp = useSpeciesNames();
  const { unitName, opstina, setOpstina, units } = useOpstina();
  const [params, setParams] = useSearchParams();
  // korisnik sa više opština (Agencija) počinje izborom opštine na karti; proizvođač i referent jedne opštine — karticama
  const view = (params.get("pogled") as View) || (units.length > 1 && !opstina && !params.get("q") ? "opstine" : "kartice");
  const setView = (v: View) => { params.set("pogled", v); setParams(params, { replace: true }); };
  const [q, setQ] = useState(params.get("q") ?? "");
  const [dq] = useDebouncedValue(q, 250);
  const [species, setSpecies] = useState<string[]>([]);
  const [crit, setCrit] = useState<Criteria>(EMPTY);
  const [advanced, setAdvanced] = useState(false);
  const setC = (patch: Partial<Criteria>) => setCrit((c) => ({ ...c, ...patch }));
  const [onMap, setOnMap] = useState(false); // „Prikaži sve na mapi“: rezultat pretrage istaknut na mapi
  const [add, setAdd] = useState(false);
  const canList = can("producers:read");

  const producers = useQuery({ queryKey: ["producers", "catalog"], enabled: canList,
    queryFn: () => http.get<Producer[]>("/api/v1/producers", { limit: 2000 }) });
  const orchards = useQuery({ queryKey: ["orchards", "catalog"], enabled: can("orchards:read"),
    queryFn: () => http.get<OrchardRow[]>("/api/v1/orchards") });
  const filterText = dq.trim().toLowerCase();
  const orchRows = useMemo(() => (orchards.data ?? []).filter((o) =>
    (!filterText || [o.name, o.producer, o.bpg, o.cultivar, o.opstina].some((x) => (x ?? "").toLowerCase().includes(filterText))) &&
    (!species.length || species.includes(o.species.toUpperCase())) && orchardMatches(o, crit)), [orchards.data, filterText, species, crit]);
  // gazdinstvo prolazi ako ima bar jedan voćnjak koji odgovara kriterijumima voćnjaka (kad su zadati)
  const holdingsWithMatch = useMemo(() => new Set((orchards.data ?? []).filter((o) => (!species.length || species.includes(o.species.toUpperCase()))
    && orchardMatches(o, crit)).map((o) => o.holding_id)), [orchards.data, species, crit]);
  const prodRows = useMemo(() => (producers.data ?? []).filter((p) =>
    (!filterText || [p.name, p.bpg, p.holder_name, p.opstina, ...(orchards.data ?? []).filter((o) => o.holding_id === p.id).flatMap((o) => [o.name, o.cultivar])]
      .some((x) => (x ?? "").toLowerCase().includes(filterText))) &&
    (!species.length || p.species.some((s) => species.includes(s.toUpperCase()))) &&
    (!orchardCriteriaSet(crit) || holdingsWithMatch.has(p.id)) &&
    (!crit.category || p.category === crit.category) && (!crit.rpg || p.rpg_status === crit.rpg) && (!crit.form || p.holding_type === crit.form)),
  [producers.data, orchards.data, filterText, species, crit, holdingsWithMatch]);
  const options = useMemo(() => {
    const u = (k: "cultivar" | "rootstock") => [...new Set((orchards.data ?? []).map((o) => o[k]).filter(Boolean) as string[])].sort();
    return { cultivars: u("cultivar"), rootstocks: u("rootstock") };
  }, [orchards.data]);
  const allSpecies = useMemo(() => {
    const m = new Map<string, number>();
    (orchards.data ?? []).forEach((o) => m.set(o.species.toUpperCase(), (m.get(o.species.toUpperCase()) ?? 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [orchards.data]);
  const resultIds = orchRows.map((o) => o.id);
  const geo = useQuery({ queryKey: ["orchards-geo", onMap ? resultIds.join(",") : ""], enabled: view === "mapa" && onMap && resultIds.length > 0,
    queryFn: () => http.get<{ type: string; features: unknown[] }>("/api/v1/orchards/geojson", { ids: resultIds.slice(0, 5000) }) });

  // eksterni korisnik bez odobrenog pristupa: katalog je prazan → zahtjev za pristup
  if (!canList) {
    return can("access:request") ? <AccessPage /> : <Empty text="Nemate pristup katalogu gazdinstava" />;
  }

  const totalArea = prodRows.reduce((s, p) => s + Number(p.area_ha || 0), 0);
  const filtered = !!filterText || species.length > 0;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <div>
          <Title order={2}>{t("Gazdinstva i voćnjaci")}</Title>
          <Text c="dimmed" size="sm">
            {fmtNum(prodRows.length, 0)} {t("gazdinstava")}, {fmtNum(orchRows.length, 0)} {t("voćnjaka")}, {fmtNum(totalArea, 1)} ha
            {opstina ? ` — ${unitName(opstina)}` : ""}
            {opstina && units.length > 1 && <Button size="compact-xs" variant="subtle" ml={6} onClick={() => { setOpstina(null); setView("opstine"); }}>
              {t("promijeni opštinu")}</Button>}
          </Text>
        </div>
        <Group gap="xs">
          {can("access:request") && !can("producers:write") && !can("analytics:read") && <Button variant="subtle" leftSection={<IconUserPlus size={16} />} onClick={() => nav("/gazdinstva?pogled=pristup")}>{t("Pristup još jednom gazdinstvu")}</Button>}
          {can("producers:write") && <Button leftSection={<IconPlus size={16} />} onClick={() => setAdd(true)}>{t("Novo gazdinstvo")}</Button>}
        </Group>
      </Group>

      <Card withBorder padding="sm">
        <Group gap="sm" wrap="wrap">
          <TextInput size="md" style={{ flex: "1 1 320px" }} leftSection={<IconSearch size={18} />} value={q}
            onChange={(e) => { setQ(e.currentTarget.value); if (view === "opstine") setView("kartice"); }}
            placeholder={t("Naziv gazdinstva, BPG, nosilac, voćnjak, sorta ili opština")} aria-label={t("Pretraga")} />
          <SegmentedControl value={view} onChange={(v) => setView(v as View)} data={[
            ...(units.length > 1 ? [{ value: "opstine", label: <Group gap={6} wrap="nowrap"><IconBuildingCommunity size={16} />{t("Opštine")}</Group> }] : []),
            { value: "kartice", label: <Group gap={6} wrap="nowrap"><IconLayoutGrid size={16} />{t("Kartice")}</Group> },
            { value: "tabela", label: <Group gap={6} wrap="nowrap"><IconTable size={16} />{t("Tabela")}</Group> },
            { value: "vocnjaci", label: <Group gap={6} wrap="nowrap"><IconTrees size={16} />{t("Voćnjaci")}</Group> },
            ...(can("map:view") ? [{ value: "mapa", label: <Group gap={6} wrap="nowrap"><IconMap2 size={16} />{t("Mapa")}</Group> }] : []),
          ]} />
        </Group>
        <Group justify="space-between" mt="sm" gap="xs">
          <Button size="xs" variant={advanced ? "light" : "subtle"} leftSection={<IconAdjustments size={14} />} onClick={() => setAdvanced((x) => !x)}
            rightSection={activeCount(crit) ? <Badge size="xs" circle>{activeCount(crit)}</Badge> : null}>{t("Napredna pretraga")}</Button>
          {(activeCount(crit) > 0 || species.length > 0 || q) && (
            <Button size="xs" variant="subtle" color="gray" onClick={() => { setCrit(EMPTY); setSpecies([]); setQ(""); }}>{t("Poništi sve kriterijume")}</Button>
          )}
        </Group>
        <Collapse expanded={advanced}>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm" mt="sm">
            <MultiSelect size="xs" label={t("Sorta")} data={options.cultivars} value={crit.cultivars} onChange={(v) => setC({ cultivars: v })} searchable clearable />
            <MultiSelect size="xs" label={t("Podloga")} data={options.rootstocks} value={crit.rootstocks} onChange={(v) => setC({ rootstocks: v })} searchable clearable />
            <Group grow gap={6}>
              <NumberInput size="xs" label={t("Sadnja od")} value={crit.yearFrom} onChange={(v) => setC({ yearFrom: v === "" ? "" : Number(v) })} min={1950} max={2100} />
              <NumberInput size="xs" label={t("do")} value={crit.yearTo} onChange={(v) => setC({ yearTo: v === "" ? "" : Number(v) })} min={1950} max={2100} />
            </Group>
            <Group grow gap={6}>
              <NumberInput size="xs" label={t("Površina voćnjaka od (ha)")} value={crit.areaFrom} onChange={(v) => setC({ areaFrom: v === "" ? "" : Number(v) })} min={0} decimalScale={2} />
              <NumberInput size="xs" label={t("do")} value={crit.areaTo} onChange={(v) => setC({ areaTo: v === "" ? "" : Number(v) })} min={0} decimalScale={2} />
            </Group>
            {([["organic", "Organska proizvodnja"], ["hailNet", "Protivgradna mreža"], ["has3d", "3D snimak"], ["photos", "Fotografije stabala"]] as const).map(([k, l]) => (
              <div key={k}>
                <Text size="xs" fw={500} mb={2}>{t(l)}</Text>
                <SegmentedControl size="xs" fullWidth value={crit[k]} onChange={(v) => setC({ [k]: v } as Partial<Criteria>)}
                  data={[{ value: "", label: t("Svejedno") }, { value: "da", label: t("Da") }, { value: "ne", label: t("Ne") }]} />
              </div>
            ))}
            <Select size="xs" label={t("Kategorija gazdinstva")} clearable value={crit.category} onChange={(v) => setC({ category: v })}
              data={[{ value: "komercijalno", label: t("Komercijalno") }, { value: "nekomercijalno", label: t("Nekomercijalno") }, { value: "pravno_lice", label: t("Pravno lice") }]} />
            <Select size="xs" label={t("Oblik")} clearable value={crit.form} onChange={(v) => setC({ form: v })}
              data={[{ value: "fizicko_lice", label: t("Fizičko lice") }, { value: "preduzetnik", label: t("Preduzetnik") }, { value: "pravno_lice", label: t("Pravno lice") }]} />
            <Select size="xs" label={t("Status u RPG")} clearable value={crit.rpg} onChange={(v) => setC({ rpg: v })}
              data={[{ value: "aktivan", label: t("Aktivan") }, { value: "pasivan", label: t("Pasivan") }]} />
          </SimpleGrid>
          <Text size="xs" c="dimmed" mt={6}>{t("Kriterijumi voćnjaka (sorta, podloga, sadnja, površina, organska, mreža, 3D, fotografije) zadržavaju gazdinstva koja imaju bar jedan takav voćnjak; opštinu birate u zaglavlju ili na karti opština.")}</Text>
        </Collapse>
        {allSpecies.length > 1 && (
          <Chip.Group multiple value={species} onChange={setSpecies}>
            <Group gap={6} mt="sm">
              {allSpecies.map(([code, n]) => (
                <Chip key={code} value={code} size="xs" color={speciesColor(code)} variant="light">
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: speciesColor(code), marginRight: 6 }} />
                  {t(sp(code))} {n}</Chip>
              ))}
            </Group>
          </Chip.Group>
        )}
      </Card>

      {view === "pristup" as View && <AccessPage />}

      {view === "opstine" && (
        <MunicipalityPicker producers={producers.data ?? []} orchards={orchards.data ?? []} selected={opstina}
          onPick={(code) => { setOpstina(code); setView("kartice"); }} />
      )}

      {view === "kartice" && (
        prodRows.length === 0 ? <Empty text={producers.isLoading ? "Učitavanje..." : "Nema gazdinstava za ovu pretragu"} /> : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="md">
            {prodRows.slice(0, 120).map((p) => <HoldingCard key={p.id} p={p} orchards={(orchards.data ?? []).filter((o) => o.holding_id === p.id)}
              onOpen={() => nav(`/gazdinstva/${p.id}`)} />)}
          </SimpleGrid>
        )
      )}
      {view === "kartice" && prodRows.length > 120 && <Text size="sm" c="dimmed">{t(`Prikazano 120 od ${prodRows.length} — suzite pretragu ili koristite tabelu.`)}</Text>}

      {view === "tabela" && (
        <DataTable rows={prodRows as unknown as Record<string, unknown>[]} exportName="gazdinstva" onRowClick={(r) => nav(`/gazdinstva/${r.id}`)} columns={[
          { key: "bpg", label: "BPG" }, { key: "name", label: "Gazdinstvo" }, { key: "holder_name", label: "Nosilac" },
          { key: "opstina", label: "Opština" }, { key: "orchard_count", label: "Voćnjaka", type: "integer" },
          { key: "area_ha", label: "Površina (ha)", type: "number" }, { key: "tree_count", label: "Stabala", type: "integer" },
          { key: "species", label: "Vrste", render: (r) => <Group gap={4}>{(r.species as string[]).map((s) => <Badge key={s} size="xs" color={speciesColor(s)} variant="light">{t(sp(s))}</Badge>)}</Group> },
          { key: "rpg_status", label: "RPG", render: (r) => <Badge size="xs" color={r.rpg_status === "aktivan" ? "green" : "gray"}>{String(r.rpg_status)}</Badge> },
        ]} />
      )}

      {view === "vocnjaci" && (
        <Stack gap="xs">
          {can("map:view") && (
            <Group justify="flex-end">
              <Button variant="light" leftSection={<IconMap2 size={16} />} disabled={!orchRows.length}
                onClick={() => { setOnMap(true); setView("mapa"); }}>
                {t(filtered ? `Prikaži rezultat na mapi (${orchRows.length})` : `Otvori sve na mapi (${orchRows.length})`)}
              </Button>
            </Group>
          )}
          <DataTable rows={orchRows as unknown as Record<string, unknown>[]} exportName="vocnjaci" onRowClick={(r) => nav(`/vocnjaci/${r.id}`)} columns={[
            { key: "name", label: "Voćnjak" },
            { key: "species", label: "Vrsta", render: (r) => <Badge size="sm" color={speciesColor(String(r.species))} variant="light">{t(sp(String(r.species)))}</Badge> },
            { key: "cultivar", label: "Sorta" }, { key: "producer", label: "Gazdinstvo" }, { key: "bpg", label: "BPG" },
            { key: "opstina", label: "Opština" }, { key: "planting_year", label: "Sadnja", type: "integer" },
            { key: "area_ha", label: "Površina (ha)", type: "number" }, { key: "tree_count", label: "Stabala", type: "integer" },
          ]} />
        </Stack>
      )}

      {view === "mapa" && (
        <Stack gap={6}>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {onMap ? t(`Istaknuto ${orchRows.length} voćnjaka iz pretrage (crveno; zeleno = ima 3D snimak).`)
                : t("Klik na voćnjak otvara karticu sa kartonom i 3D prikazom; klik na parcelu prikazuje voćnjake na njoj.")}
            </Text>
            {onMap ? <Button size="xs" variant="subtle" onClick={() => setOnMap(false)}>{t("Ukloni isticanje")}</Button>
              : <Button size="xs" variant="light" leftSection={<IconMap2 size={14} />} disabled={!orchRows.length} onClick={() => setOnMap(true)}>
                {t(filtered ? `Istakni rezultat pretrage (${orchRows.length})` : `Istakni sve voćnjake (${orchRows.length})`)}</Button>}
          </Group>
          <MapPage height="calc(100dvh - 300px)" results={onMap ? geo.data ?? null : null} />
        </Stack>
      )}

      <Modal opened={add} onClose={() => setAdd(false)} title={t("Novo gazdinstvo")} size="lg">
        <ProducerForm onDone={() => setAdd(false)} />
      </Modal>
    </Stack>
  );
}

/** Kartica gazdinstva: vrste kao obojena traka po površini, ključni brojevi, 3D oznaka. */
function HoldingCard({ p, orchards, onOpen }: { p: Producer; orchards: OrchardRow[]; onOpen: () => void }) {
  const { t } = useT();
  const sp = useSpeciesNames();
  const bySpecies = new Map<string, number>();
  orchards.forEach((o) => bySpecies.set(o.species.toUpperCase(), (bySpecies.get(o.species.toUpperCase()) ?? 0) + Number(o.area_ha || 0)));
  const total = [...bySpecies.values()].reduce((a, b) => a + b, 0) || 1;
  return (
    <Card withBorder padding="md" radius="lg" className="holding-card" onClick={onOpen} tabIndex={0} role="link"
      onKeyDown={(e) => e.key === "Enter" && onOpen()} aria-label={`${p.name}, BPG ${p.bpg}`}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb={6}>
        <div style={{ minWidth: 0 }}>
          <Text fw={750} lineClamp={1}>{p.name}</Text>
          <Text size="xs" c="dimmed" lineClamp={1}>BPG {p.bpg} · {t(p.opstina ?? "")}</Text>
        </div>
        {p.rpg_status !== "aktivan" && <Badge size="xs" color="gray">{p.rpg_status}</Badge>}
      </Group>
      <div className="species-bar" aria-hidden>
        {[...bySpecies.entries()].map(([s, a]) => <span key={s} style={{ width: `${(a / total) * 100}%`, background: speciesColor(s) }} />)}
      </div>
      <Group gap={6} mt={8} mb={10}>
        {[...bySpecies.keys()].slice(0, 4).map((s) => (
          <Group key={s} gap={4} wrap="nowrap"><span style={{ width: 8, height: 8, borderRadius: 4, background: speciesColor(s) }} />
            <Text size="xs">{t(sp(s))}</Text></Group>
        ))}
      </Group>
      <Group justify="space-between" gap={4}>
        <Stat v={fmtNum(p.orchard_count, 0)} l={t("voćnjaka")} />
        <Stat v={`${fmtNum(p.area_ha, 1)}`} l="ha" />
        <Stat v={fmtNum(p.tree_count, 0)} l={t("stabala")} />
        {(p as Producer & { has_3d?: boolean }).has_3d && <Tooltip label={t("Ima 3D snimak")}><IconCube size={18} color="#2f9e44" /></Tooltip>}
      </Group>
    </Card>
  );
}

function Stat({ v, l }: { v: string; l: string }) {
  return (
    <div>
      <Text fw={750} size="lg" lh={1.1}>{v}</Text>
      <Text size="xs" c="dimmed">{l}</Text>
    </div>
  );
}


/** Početni izbor opštine (Agencija): karta gradova i opština sa brojem gazdinstava + lista; klik → katalog te opštine. */
function MunicipalityPicker({ producers, orchards, selected, onPick }: {
  producers: Producer[]; orchards: OrchardRow[]; selected: string | null; onPick: (code: string) => void;
}) {
  const { t } = useT();
  const [metric, setMetric] = useState<"gazdinstava" | "vocnjaka" | "povrsina">("gazdinstava");
  const geo = useQuery({ queryKey: ["opstine-geo"], staleTime: 600_000,
    queryFn: () => http.get<{ features: { type: string; geometry: unknown; properties: { code: string; name: string } }[] }>("/api/v1/public/stats/map") });
  const stats = useMemo(() => {
    const m = new Map<string, { code: string; name: string; gazdinstava: number; vocnjaka: number; povrsina: number; vrste: Set<string> }>();
    producers.forEach((p) => {
      const k = p.opstina_code ?? "?";
      const r = m.get(k) ?? { code: k, name: p.opstina ?? k, gazdinstava: 0, vocnjaka: 0, povrsina: 0, vrste: new Set<string>() };
      r.gazdinstava += 1; r.vocnjaka += p.orchard_count; r.povrsina += Number(p.area_ha || 0); p.species.forEach((s) => r.vrste.add(s));
      m.set(k, r);
    });
    return [...m.values()].sort((a, b) => b.gazdinstava - a.gazdinstava);
  }, [producers]);
  const byCode = new Map(stats.map((s) => [s.code, s]));
  const data = geo.data ? { type: "FeatureCollection", label: { gazdinstava: "gazdinstava", vocnjaka: "voćnjaka", povrsina: "ha" }[metric], features: geo.data.features.map((f) => ({ ...f, properties: { ...f.properties,
    value: byCode.get(f.properties.code)?.[metric] ?? null } })) } : undefined;
  void orchards;
  return (
    <Grid>
      <Grid.Col span={{ base: 12, lg: 8 }}>
        <Card withBorder radius="lg" padding="sm">
          <Group justify="space-between" mb="xs">
            <Text fw={700}>{t("Izaberite grad ili opštinu")}</Text>
            <SegmentedControl size="xs" value={metric} onChange={(v) => setMetric(v as typeof metric)} data={[
              { value: "gazdinstava", label: t("Gazdinstva") }, { value: "vocnjaka", label: t("Voćnjaci") }, { value: "povrsina", label: "ha" }]} />
          </Group>
          <ChoroplethMap data={data as never} height={520} selected={selected} onSelect={onPick} />
        </Card>
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Stack gap="xs">
          {stats.map((s) => (
            <Card key={s.code} withBorder radius="md" padding="sm" className="holding-card" tabIndex={0} role="link"
              onClick={() => onPick(s.code)} onKeyDown={(e) => e.key === "Enter" && onPick(s.code)}>
              <Group justify="space-between" wrap="nowrap">
                <div>
                  <Text fw={750}>{t(s.name)}</Text>
                  <Group gap={4} mt={4}>
                    {[...s.vrste].slice(0, 6).map((v) => <span key={v} title={v} style={{ width: 9, height: 9, borderRadius: 5, background: speciesColor(v) }} />)}
                  </Group>
                </div>
                <Group gap="md" wrap="nowrap">
                  <Stat v={fmtNum(s.gazdinstava, 0)} l={t("gazd.")} />
                  <Stat v={fmtNum(s.vocnjaka, 0)} l={t("voćnj.")} />
                  <Stat v={fmtNum(s.povrsina, 1)} l="ha" />
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      </Grid.Col>
    </Grid>
  );
}
